import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Plus, Copy, Check, Loader2, Trash2, Shield, AlertTriangle, RefreshCw } from 'lucide-react';
import { getToken } from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL;

function AdminInternalDistribution() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [codeCount, setCodeCount] = useState(5);
  const [copiedCode, setCopiedCode] = useState(null);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const res = await fetch(`${API}/api/admin/universal-codes`, {
        credentials: 'include',
        headers
      });

      if (res.status === 403) {
        setIsAdmin(false);
        setError('Admin access required. This page is only accessible by app administrators.');
        setLoading(false);
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setCodes(data.codes || []);
        setStats({
          total: data.total_generated,
          unused: data.unused_count,
          used: data.used_count,
          max: data.max_codes,
          remaining: data.remaining
        });
        setIsAdmin(true);
      }
    } catch (error) {
      console.error('Error fetching codes:', error);
      setError('Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  const generateCodes = async () => {
    setGenerating(true);
    setError('');
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/admin/universal-codes/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ count: codeCount })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Failed to generate codes');
        return;
      }

      // Refresh the list
      fetchCodes();
    } catch (error) {
      console.error('Error generating codes:', error);
      setError('Failed to generate codes');
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const revokeCode = async (code) => {
    if (!window.confirm(`Revoke code ${code}? This cannot be undone.`)) return;

    try {
      const token = getToken();
      const res = await fetch(`${API}/api/admin/universal-codes/${code}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (res.ok) {
        fetchCodes();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to revoke code');
      }
    } catch (error) {
      console.error('Error revoking code:', error);
      setError('Failed to revoke code');
    }
  };

  const revokeLifetimeAccess = async (code) => {
    const confirmed = window.confirm(
      `⚠️ REVOKE LIFETIME ACCESS\n\nThis will:\n• Remove the user's premium subscription\n• Downgrade them to the free tier\n• Mark the code as revoked (cannot be reused)\n\nAre you sure you want to continue?`
    );
    
    if (!confirmed) return;

    try {
      const token = getToken();
      const res = await fetch(`${API}/api/admin/revoke-lifetime?code=${encodeURIComponent(code)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Failed to revoke access');
        return;
      }

      alert(`✅ ${data.message}`);
      fetchCodes();
    } catch (error) {
      console.error('Error revoking access:', error);
      setError('Failed to revoke access');
    }
  };

  const unusedCodes = codes.filter(c => c.status === 'unused');
  const usedCodes = codes.filter(c => c.status === 'used');
  const revokedCodes = codes.filter(c => c.status === 'revoked');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-4">
        <div className="max-w-2xl mx-auto pt-20 text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <button
            onClick={() => navigate('/app/dashboard')}
            className="px-6 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white pb-20">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white mr-4"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-amber-500 flex items-center gap-2">
              <Crown className="w-6 h-6" />
              Admin: Universal Lifetime Codes
            </h1>
            <p className="text-sm text-gray-400">God Mode - Generate permanent access codes</p>
          </div>
          <button
            onClick={fetchCodes}
            className="p-2 text-gray-400 hover:text-white"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Warning Banner */}
        <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-amber-500">Admin Only - Handle With Care</h3>
              <p className="text-sm text-amber-200/80 mt-1">
                These codes grant <strong>PERMANENT lifetime access</strong> that never expires, 
                regardless of any subscription status. They are NOT tied to any payment.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        {stats && (
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="font-bold text-lg mb-4">Code Statistics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-700 rounded-xl">
                <p className="text-3xl font-bold text-amber-500">{stats.total}</p>
                <p className="text-sm text-gray-400">Total Generated</p>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-xl">
                <p className="text-3xl font-bold text-green-500">{stats.unused}</p>
                <p className="text-sm text-gray-400">Available</p>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-xl">
                <p className="text-3xl font-bold text-blue-500">{stats.used}</p>
                <p className="text-sm text-gray-400">Redeemed</p>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-xl">
                <p className="text-3xl font-bold text-purple-500">{stats.remaining}</p>
                <p className="text-sm text-gray-400">Remaining (of {stats.max})</p>
              </div>
            </div>
          </div>
        )}

        {/* Generate Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-amber-500" />
            Generate New Codes
          </h2>
          
          {error && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-1">Number of codes</label>
              <input
                type="number"
                value={codeCount}
                onChange={(e) => setCodeCount(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                min="1"
                max="20"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white"
              />
            </div>
            <button
              onClick={generateCodes}
              disabled={generating || (stats && stats.remaining < codeCount)}
              className="px-6 py-3 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mt-6"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5" />
                  Generate
                </>
              )}
            </button>
          </div>
        </div>

        {/* Unused Codes */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="px-6 py-4 bg-green-900/30 border-b border-gray-700">
            <h2 className="font-bold text-green-500">
              Available Codes ({unusedCodes.length})
            </h2>
            <p className="text-sm text-gray-400">Ready to distribute</p>
          </div>
          
          {unusedCodes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No available codes. Generate some above.
            </div>
          ) : (
            <div className="divide-y divide-gray-700 max-h-96 overflow-y-auto">
              {unusedCodes.map((code) => (
                <div key={code.code} className="px-6 py-4 flex items-center justify-between hover:bg-gray-700/50">
                  <div>
                    <p className="font-mono text-lg text-amber-500 font-bold">{code.code}</p>
                    <p className="text-xs text-gray-500">
                      Created: {new Date(code.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyCode(code.code)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg"
                      title="Copy code"
                    >
                      {copiedCode === code.code ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => revokeCode(code.code)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg"
                      title="Revoke code"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Used Codes */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="px-6 py-4 bg-blue-900/30 border-b border-gray-700">
            <h2 className="font-bold text-blue-500">
              Redeemed Codes ({usedCodes.length})
            </h2>
            <p className="text-sm text-gray-400">These users have permanent lifetime access</p>
          </div>
          
          {usedCodes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No codes have been redeemed yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-700 max-h-96 overflow-y-auto">
              {usedCodes.map((code) => (
                <div key={code.code} className="px-6 py-4 hover:bg-gray-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-mono text-gray-400">{code.code}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-blue-900/50 text-blue-400 px-2 py-1 rounded-full">
                        Redeemed
                      </span>
                      <button
                        onClick={() => revokeLifetimeAccess(code.code)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg font-medium transition-colors"
                        title="Revoke this user's lifetime access"
                        data-testid={`revoke-access-${code.code}`}
                      >
                        Revoke Access
                      </button>
                    </div>
                  </div>
                  <div className="text-sm">
                    <p className="text-white font-medium">{code.used_by_name || 'Unknown'}</p>
                    <p className="text-gray-500">{code.used_by_email}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Redeemed: {new Date(code.used_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Revoked Codes */}
        {revokedCodes.length > 0 && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="px-6 py-4 bg-red-900/30 border-b border-gray-700">
              <h2 className="font-bold text-red-500">
                Revoked Codes ({revokedCodes.length})
              </h2>
              <p className="text-sm text-gray-400">Access has been revoked for these codes</p>
            </div>
            <div className="divide-y divide-gray-700 max-h-48 overflow-y-auto">
              {revokedCodes.map((code) => (
                <div key={code.code} className="px-6 py-4 opacity-60">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-mono text-gray-500 line-through">{code.code}</p>
                    <span className="text-xs bg-red-900/50 text-red-400 px-2 py-1 rounded-full">
                      Revoked
                    </span>
                  </div>
                  <div className="text-sm">
                    <p className="text-gray-400">{code.used_by_email || 'Unknown'}</p>
                    <p className="text-xs text-gray-600">
                      Revoked: {code.revoked_at ? new Date(code.revoked_at).toLocaleDateString() : 'Unknown'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminInternalDistribution;
